import { useState } from 'react';
import { getRebusSession } from '../../../services/rebusSessionService';
import { getRebus } from '../../../services/rebusService';

export default function StationJoinScreen({ onJoin }) {
  const [step, setStep] = useState(1);
  const [pin, setPin] = useState('');
  const [stations, setStations] = useState([]);
  const [selectedStation, setSelectedStation] = useState('');
  const [leaderName, setLeaderName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessionData, setSessionData] = useState(null);

  const handlePinSubmit = async () => {
    if (pin.length < 4) {
      setError('Skriv inn ein gyldig PIN-kode');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const session = await getRebusSession(pin.toUpperCase());
      setSessionData(session);

      const rebus = await getRebus(session.rebusId);
      const stationList = (rebus.stations || []).map((s) => ({
        id: s.id,
        name: s.name,
        order: s.order
      }));
      setStations(stationList);
      setStep(2);
    } catch (err) {
      setError(err.message || 'Fann ikkje Mikkelchase-økta. Sjekk koden.');
    } finally {
      setLoading(false);
    }
  };

  const handleStationSelect = () => {
    if (!selectedStation) {
      setError('Vel ein stasjon');
      return;
    }
    setError(null);
    setStep(3);
  };

  const handleJoin = () => {
    const trimmed = leaderName.trim();
    if (trimmed.length < 2) {
      setError('Namn ma vere minst 2 teikn');
      return;
    }
    setError(null);
    onJoin(pin.toUpperCase(), selectedStation, trimmed);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-600 to-teal-800 flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-white/15 backdrop-blur-md rounded-2xl flex items-center justify-center mx-auto mb-4 border border-white/20">
            <span className="material-symbols-outlined text-white text-3xl" style={{ fontVariationSettings: "'FILL' 1" }}>
              location_on
            </span>
          </div>
          <h1 className="text-white text-2xl font-extrabold">Stasjonsleiar</h1>
          <p className="text-teal-200 text-sm mt-1">Bli med som stasjonsleiar i ein Mikkelchase</p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`h-2 rounded-full transition-all ${
                s === step ? 'w-8 bg-white' : s < step ? 'w-2 bg-white/70' : 'w-2 bg-white/20'
              }`}
            />
          ))}
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          {/* Step 1: PIN */}
          {step === 1 && (
            <>
              <label className="block text-gray-600 text-sm font-bold mb-2">Mikkelchase-kode</label>
              <input
                type="text"
                inputMode="text"
                maxLength={6}
                value={pin}
                onChange={(e) => {
                  setPin(e.target.value.toUpperCase());
                  setError(null);
                }}
                onKeyDown={(e) => e.key === 'Enter' && handlePinSubmit()}
                placeholder="T.d. ABC123"
                className="w-full px-4 py-4 text-2xl font-bold text-center ghost-border ghost-border-focus rounded-xl tracking-[0.3em] uppercase"
                autoFocus
              />
              <button
                onClick={handlePinSubmit}
                disabled={loading || pin.length < 4}
                className="w-full mt-4 bg-gradient-to-r from-eq-primary to-eq-primary-container text-white py-4 rounded-xl font-bold text-lg hover:brightness-110 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="material-symbols-outlined animate-spin text-xl">progress_activity</span>
                    Lastar...
                  </span>
                ) : (
                  'Neste'
                )}
              </button>
            </>
          )}

          {/* Step 2: Select station */}
          {step === 2 && (
            <>
              <label className="block text-gray-600 text-sm font-bold mb-2">Vel stasjon</label>
              <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
                {stations.map((s) => {
                  const taken = sessionData?.stationLeaders?.[s.id];
                  return (
                    <button
                      key={s.id}
                      onClick={() => {
                        if (!taken) {
                          setSelectedStation(s.id);
                          setError(null);
                        }
                      }}
                      disabled={!!taken}
                      className={`w-full text-left px-4 py-3 rounded-xl transition-all ${
                        taken
                          ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
                          : selectedStation === s.id
                            ? 'ring-2 ring-teal-500 bg-teal-50 text-teal-800 shadow-ambient'
                            : 'shadow-ambient hover:shadow-lg hover:bg-teal-50/50'
                      }`}
                    >
                      <span className="font-bold">Stasjon {s.order}:</span>{' '}
                      <span>{s.name}</span>
                      {taken && (
                        <span className="block text-xs text-gray-400 mt-0.5">
                          Oppteken av {taken.name}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setStep(1)}
                  className="px-4 py-3 rounded-xl shadow-ambient text-gray-600 font-semibold hover:bg-gray-50 transition-all"
                >
                  Tilbake
                </button>
                <button
                  onClick={handleStationSelect}
                  disabled={!selectedStation}
                  className="flex-1 bg-teal-600 text-white py-3 rounded-xl font-bold text-lg hover:bg-teal-700 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Neste
                </button>
              </div>
            </>
          )}

          {/* Step 3: Name */}
          {step === 3 && (
            <>
              <label className="block text-gray-600 text-sm font-bold mb-2">Ditt namn</label>
              <input
                type="text"
                value={leaderName}
                onChange={(e) => {
                  setLeaderName(e.target.value);
                  setError(null);
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleJoin()}
                placeholder="T.d. Ola Nordmann"
                className="w-full px-4 py-4 text-lg font-semibold ghost-border ghost-border-focus rounded-xl"
                autoFocus
              />
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => setStep(2)}
                  className="px-4 py-3 rounded-xl shadow-ambient text-gray-600 font-semibold hover:bg-gray-50 transition-all"
                >
                  Tilbake
                </button>
                <button
                  onClick={handleJoin}
                  disabled={leaderName.trim().length < 2}
                  className="flex-1 bg-gradient-to-r from-eq-primary to-eq-primary-container text-white py-4 rounded-xl font-bold text-lg hover:brightness-110 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Bli med som stasjonsleiar
                </button>
              </div>
            </>
          )}

          {/* Error */}
          {error && (
            <p className="mt-3 text-red-500 text-sm font-medium flex items-center gap-1">
              <span className="material-symbols-outlined text-sm">error</span>
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
